import { ref, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import type { ProfileGroup, RiskModelInstance, RiskProfileCreatePayload, RiskProfileInstanceDraftPayload, BaseForWizard, ProfileGroupListParams, PaginatedProfileGroups } from '@/types/risk-profile';

import { LifecycleStatus } from '@/types/enums';
import { VersioningManagerActiveView, type PolicyVisibilityFilter } from '@/types/versioning';

// --- INTERFACES ---

interface ApiEndpoints {
    // Server-paginated/filtered list fetch.
    fetchData: (params?: ProfileGroupListParams) => Promise<PaginatedProfileGroups>;
    // Fetches one group with its full instance config (details/edit views).
    fetchOne: (id: string) => Promise<ProfileGroup>;
    create: (payload: RiskProfileCreatePayload) => Promise<any>;
    createDraft: (groupId: string, payload: RiskProfileInstanceDraftPayload) => Promise<any>;
    promote: (groupId: string, instanceId: string) => Promise<any>;
    activate: (groupId: string) => Promise<any>;
    deactivate: (groupId: string) => Promise<any>;
    setAsProduction: (groupId: string, instanceId: string) => Promise<any>;
    discardDraft?: (groupId: string) => Promise<any>;
    setDesignated: (groupId: string, instanceId: string) => Promise<any>;
    delete?: (groupId: string) => Promise<any>;
}


export function useRiskProfileManager(api: ApiEndpoints) {
    // --- HELPERS (from former useRiskModelManager) ---
    /**
     * Filters a group's instances to return only releases (i.e., not drafts).
     * @param group The profile group to process.
     * @returns An array of release instances.
     */
    const getReleases = (group: ProfileGroup): RiskModelInstance[] => {
        if (!group?.instances) return [];
        return group.instances.filter(i => i.instance_lifecycle !== LifecycleStatus.DRAFT);
    };

    /**
     * Finds the draft instance within a profile group.
     * @param group The profile group to process.
     * @returns The draft instance or undefined if not found.
     */
    const getDraft = (group: ProfileGroup): RiskModelInstance | undefined => {
        if (!group?.instances) return undefined;
        return group.instances.find(i => i.instance_lifecycle === LifecycleStatus.DRAFT);
    };

    /**
     * Finds the currently active production instance for a group.
     * @param group The profile group to process.
     * @returns The active instance or undefined if not found.
     */
    const getCurrentInstance = (group: ProfileGroup): RiskModelInstance | undefined => {
        const releases = getReleases(group);
        if (!releases.length || !group.currentInstanceId) return undefined;
        return releases.find(r => r.id === group.currentInstanceId);
    };

    /**
     * Finds the most recent release (highest version number) for a group.
     * @param group The profile group to process.
     * @returns The latest release instance or null if no releases exist.
     */
    const getLatestRelease = (group: ProfileGroup): RiskModelInstance | null => {
        const releases = getReleases(group);
        if (!releases.length) return null;
        return releases.reduce((a, b) => ((a.version_number || 0) > (b.version_number || 0) ? a : b));
    };


    // --- STATE ---
    const profileGroups: Ref<ProfileGroup[]> = ref([]);
    const isLoading: Ref<boolean> = ref(false);
    const activeView: Ref<VersioningManagerActiveView> = ref(VersioningManagerActiveView.LIST);
    const selectedProfileGroup: Ref<ProfileGroup | null> = ref(null);
    const baseForWizard: Ref<BaseForWizard | null> = ref(null);
    const searchQuery: Ref<string> = ref('');
    const isSaving: Ref<boolean> = ref(false);
    const error: Ref<string | null> = ref(null);

    // --- SERVER PAGINATION / SORT / FILTER STATE ---
    // `first` is the 0-based row offset (matches PrimeVue's paginator `first`).
    const totalRecords: Ref<number> = ref(0);
    const first: Ref<number> = ref(0);
    const pageSize: Ref<number> = ref(10);
    const statusFilter: Ref<PolicyVisibilityFilter> = ref('active');
    // On/off filter: null = both.
    const isActiveFilter: Ref<boolean | null> = ref(null);
    // Backend sort field name ('name' | 'entity_type' | 'created_at' | 'updated_at') or null.
    const sortField: Ref<string | null> = ref(null);
    const sortOrder: Ref<'asc' | 'desc'> = ref('desc');

    // --- GETTERS ---
    // Search/filtering now happen server-side, so this simply mirrors the current page.
    // Kept for backwards compatibility with existing template bindings.
    const filteredProfileGroups: ComputedRef<ProfileGroup[]> = computed(() => profileGroups.value);

    // --- ACTIONS ---

    function buildListParams(): ProfileGroupListParams {
        return {
            offset: first.value,
            limit: pageSize.value,
            search: searchQuery.value.trim() || undefined,
            status: statusFilter.value,
            is_active: isActiveFilter.value ?? undefined,
            sort_field: sortField.value ?? undefined,
            sort_order: sortOrder.value,
        };
    }

    async function fetchData(): Promise<void> {
        isLoading.value = true;
        try {
            const res = await api.fetchData(buildListParams());
            profileGroups.value = res.items;
            totalRecords.value = res.total;
        } catch (err) {
            console.error('Failed to fetch profile groups:', err);
            profileGroups.value = [];
            totalRecords.value = 0;
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Fetches a single group with its full instance config and selects it.
     * Also syncs the matching row in the current page so summary getters stay accurate.
     */
    async function fetchProfileGroup(groupId: string): Promise<ProfileGroup | undefined> {
        const group = await api.fetchOne(groupId);
        const idx = profileGroups.value.findIndex(g => g.profileGroupId === groupId);
        if (idx !== -1) profileGroups.value[idx] = group;
        selectedProfileGroup.value = group;
        return group;
    }

    // --- LIST CONTROL HELPERS (used by the list views) ---
    function setPage(offset: number, rows: number): Promise<void> {
        first.value = offset;
        pageSize.value = rows;
        return fetchData();
    }

    function setSort(field: string | null, order: 'asc' | 'desc'): Promise<void> {
        sortField.value = field;
        sortOrder.value = order;
        first.value = 0;
        return fetchData();
    }

    function setSearch(query: string): Promise<void> {
        searchQuery.value = query;
        first.value = 0;
        return fetchData();
    }

    function setStatusFilter(status: PolicyVisibilityFilter): Promise<void> {
        statusFilter.value = status;
        first.value = 0;
        return fetchData();
    }

    function setIsActiveFilter(isActive: boolean | null): Promise<void> {
        isActiveFilter.value = isActive;
        first.value = 0;
        return fetchData();
    }

    function navigateToProfileDetails(group: ProfileGroup): void {
        selectedProfileGroup.value = group;
        activeView.value = VersioningManagerActiveView.DETAILS;
    }

    function navigateToAddProfile(): void {
        selectedProfileGroup.value = null;
        baseForWizard.value = null;
        activeView.value = VersioningManagerActiveView.WIZARD;
    }

    function navigateToList(): void {
        selectedProfileGroup.value = null;
        baseForWizard.value = null;
        activeView.value = VersioningManagerActiveView.LIST;
    }

    function handleCancelWizard(): void {
        if (selectedProfileGroup.value) {
            activeView.value = VersioningManagerActiveView.DETAILS;
        } else {
            navigateToList();
        }
    }

    async function saveNewProfile(profileData: RiskProfileCreatePayload): Promise<void> {
        isSaving.value = true;
        error.value = null;
        try {
            const newProfile = await api.create(profileData);
            const updatedGroup = await fetchProfileGroup(newProfile.id);
            if (updatedGroup) {
                navigateToProfileDetails(updatedGroup);
            } else {
                navigateToList();
            }
        } catch (err: any) {
            console.error("Failed to save configuration:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred.';
            throw err;
        } finally {
            isSaving.value = false;
        }
    }

    async function saveNewDraftInstance(draftData: any): Promise<void> {
        isSaving.value = true;
        error.value = null;
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) {
            error.value = 'No profile group selected.';
            isSaving.value = false;
            return;
        }

        try {
            await api.createDraft(groupId, draftData);
            const updatedGroup = await fetchProfileGroup(groupId);
            if (updatedGroup) {
                navigateToProfileDetails(updatedGroup);
            } else {
                navigateToList();
            }
        } catch (err: any) {
            console.error("Failed to save draft instance:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred while saving the draft.';
            throw err;
        } finally {
            isSaving.value = false;
        }
    }

    async function promoteToProduction(instanceId: string): Promise<void> {
        isSaving.value = true;
        error.value = null;
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) {
            error.value = 'No profile group selected.';
            isSaving.value = false;
            return;
        }

        try {
            await api.promote(groupId, instanceId);
            const updatedGroup = await fetchProfileGroup(groupId);
            if (updatedGroup) {
                navigateToProfileDetails(updatedGroup);
            } else {
                navigateToList();
            }
        } catch (err: any) {
            console.error("Failed to promote draft:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred while promoting the draft.';
            throw err;
        } finally {
            isSaving.value = false;
        }
    }

    async function activateRiskModel(): Promise<void> {
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) return;
        await api.activate(groupId);
        selectedProfileGroup.value = (await fetchProfileGroup(groupId)) || null;
    }

    async function deactivateRiskModel(): Promise<void> {
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) return;
        await api.deactivate(groupId);
        selectedProfileGroup.value = (await fetchProfileGroup(groupId)) || null;
    }

    async function setReleaseAsProduction(instanceId: string): Promise<void> {
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) return;
        isSaving.value = true;
        error.value = null;
        try {
            await api.setAsProduction(groupId, instanceId);
            const updatedGroup = await fetchProfileGroup(groupId);
            if (updatedGroup) {
                selectedProfileGroup.value = updatedGroup;
            }
        } catch (err: any) {
            console.error("Failed to set release as production:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred.';
        } finally {
            isSaving.value = false;
        }
    }

    async function discardDraft(): Promise<void> {
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) return;
        await api.discardDraft!(groupId);
        const updatedGroup = await fetchProfileGroup(groupId);
        if (updatedGroup) {
            navigateToProfileDetails(updatedGroup);
        } else {
            navigateToList();
        }
    }

    async function setDesignatedInstance(instanceId: string): Promise<void> {
        const groupId = selectedProfileGroup.value?.profileGroupId;
        if (!groupId) return;
        try {
            await api.setDesignated(groupId, instanceId);
            const updatedGroup = await fetchProfileGroup(groupId);
            if (updatedGroup) {
                selectedProfileGroup.value = updatedGroup;
            }
        } catch (err: any) {
            console.error("Failed to set designated instance:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred.';
        }
    }

    async function deleteProfile(groupId: string): Promise<void> {
        isSaving.value = true;
        error.value = null;
        try {
            await api.delete!(groupId);
            await fetchData();
            if (selectedProfileGroup.value?.profileGroupId === groupId) {
                navigateToList();
            }
        } catch (err: any) {
            console.error("Failed to delete profile:", err);
            error.value = err.response?.data?.detail || 'An unknown error occurred while deleting the profile.';
            throw err;
        } finally {
            isSaving.value = false;
        }
    }

    function createDraftFromRelease(group: ProfileGroup, releaseId: string): void {
        const releaseToClone = group.instances.find(i => i.id === releaseId);
        if (!releaseToClone) {
            console.error(`Could not find release with id ${releaseId} to clone.`);
            return;
        }

        selectedProfileGroup.value = group;
        baseForWizard.value = {
            isEditMode: true,
            isClone: false,
            name: group.profileName,
            originalName: group.profileName,
            entityType: group.entity_type,
            entity_scope: releaseToClone.entity_scope,
            relationship_type_ids: group.relationship_types?.map((rt: any) => rt.id),
            configuration: JSON.parse(JSON.stringify(releaseToClone.categories)),
            based_on_instance_id: releaseToClone.id,
            default_outdated_threshold_value: releaseToClone.default_outdated_threshold_value,
            default_outdated_threshold_type: releaseToClone.default_outdated_threshold_type,
            base_token_price: releaseToClone.base_token_price,
            decision_logic: releaseToClone.decision_logic ?? null,
        };
        activeView.value = VersioningManagerActiveView.WIZARD;
    }

    function navigateToEditDraft(group: ProfileGroup, instance_id: string): void {
        selectedProfileGroup.value = group;
        if (!group?.instances) return;
        const draft = group.instances.find(i => i.id === instance_id && i.instance_lifecycle === LifecycleStatus.DRAFT);
        if (draft) {
            baseForWizard.value = {
                isEditMode: true,
                isClone: false,
                name: group.profileName,
                originalName: group.profileName,
                entityType: group.entity_type,
                entity_scope: draft.entity_scope,
                relationship_type_ids: group.relationship_types?.map((rt: any) => rt.id),
                configuration: draft.categories,
                based_on_instance_id: draft.based_on_instance_id,
                default_outdated_threshold_value: draft.default_outdated_threshold_value,
                default_outdated_threshold_type: draft.default_outdated_threshold_type,
                base_token_price: draft.base_token_price,
                decision_logic: draft.decision_logic ?? null,
            };
            activeView.value = VersioningManagerActiveView.WIZARD;
        }
    }

    function cloneProfile(profileToClone: ProfileGroup, releaseToClone: RiskModelInstance): void {
        if (!releaseToClone) return;
        selectedProfileGroup.value = null;
        baseForWizard.value = {
            isEditMode: false,
            isClone: true,
            name: null,
            originalName: profileToClone.profileName,
            entityType: profileToClone.entity_type,
            entity_scope: releaseToClone.entity_scope,
            relationship_type_ids: profileToClone.relationship_types?.map((rt:any) => rt.id),
            configuration: JSON.parse(JSON.stringify(releaseToClone.categories)),
            based_on_instance_id: null,
            default_outdated_threshold_value: releaseToClone.default_outdated_threshold_value,
            default_outdated_threshold_type: releaseToClone.default_outdated_threshold_type,
            base_token_price: releaseToClone.base_token_price,
            decision_logic: releaseToClone.decision_logic ?? null,
        };
        activeView.value = VersioningManagerActiveView.WIZARD;
    }

    return {
        // State
        profileGroups,
        isLoading,
        activeView,
        selectedProfileGroup,
        baseForWizard,
        searchQuery,
        isSaving,
        error,
        // Pagination / sort / filter state
        totalRecords,
        first,
        pageSize,
        statusFilter,
        isActiveFilter,
        sortField,
        sortOrder,
        // Getters
        filteredProfileGroups,
        // Actions
        fetchData,
        fetchProfileGroup,
        setPage,
        setSort,
        setSearch,
        setStatusFilter,
        setIsActiveFilter,
        saveNewProfile,
        navigateToProfileDetails,
        navigateToAddProfile,
        navigateToList,
        handleCancelWizard,
        navigateToEditDraft,
        createDraftFromRelease,
        discardDraft,
        promoteToProduction,
        deactivateRiskModel,
        activateRiskModel,
        setDesignatedInstance,
        setReleaseAsProduction,
        deleteProfile,
        cloneProfile,
        saveNewDraftInstance,
        // Composable Getters
        getReleases,
        getDraft,
        getCurrentInstance,
        getLatestRelease,
    };
}
